import { useState } from "react";
import type { MusteriKayit, MusteriKayitDurum } from "../../../mocks/musteriPaneli";
import { durumRenk } from "../../../mocks/musteriPaneli";
import DeliveryTracker from "./DeliveryTracker";

interface Props {
  kayit: MusteriKayit;
  isCompleted?: boolean;
}

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "—";

type Step = { label: string; icon: string };

const STEPS: Step[] = [
  { label: "Kayıt", icon: "ri-file-add-line" },
  { label: "Kesim Sırası", icon: "ri-calendar-check-line" },
  { label: "Hazırlanıyor", icon: "ri-scissors-2-line" },
  { label: "Teslimat", icon: "ri-truck-line" },
  { label: "Teslim", icon: "ri-checkbox-circle-line" },
];

function getStep(durum: MusteriKayitDurum): number {
  if (durum === "Aktif") return 0;
  if (durum === "Kesim Bekliyor") return 1;
  if (["Kesime Girdi", "Kesim Yapıldı", "Parçalanıyor"].includes(durum)) return 2;
  if (["Hazır", "Taşıma Halinde"].includes(durum)) return 3;
  return 4;
}

export default function ShareCard({ kayit, isCompleted = false }: Props) {
  const [showOdemeler, setShowOdemeler] = useState(false);

  const rInfo = durumRenk[kayit.durum];
  const stepIdx = getStep(kayit.durum);
  const pct =
    kayit.toplamTutar > 0
      ? Math.round((kayit.odenen / kayit.toplamTutar) * 100)
      : 100;

  const hizmetTutar =
    kayit.kesimUcreti +
    kayit.parcalamaUcreti +
    kayit.tasimaUcreti -
    kayit.indirim;

  return (
    <div
      className={`bg-farm-warm border border-farm-border rounded-farm overflow-hidden ${
        isCompleted ? "opacity-90" : ""
      }`}
    >
      <div
        className={`px-4 py-2.5 flex items-center justify-between ${
          isCompleted ? "bg-farm-green-pale/40" : "bg-farm-cream"
        } border-b border-farm-border`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${rInfo.badge}`}
          >
            <i className={`${rInfo.icon} text-xs`} />
            {rInfo.label}
          </span>

          {kayit.kesimTarihi && !isCompleted && (
            <span className="text-[10px] text-farm-muted">
              Kesim: {fmtDate(kayit.kesimTarihi)}
              {kayit.kesimSaati ? ` · ${kayit.kesimSaati}` : ""}
            </span>
          )}

          {isCompleted && (kayit.tamamlanmaTarihi || kayit.teslimat?.teslimSaati) && (
            <span className="text-[10px] text-farm-green font-semibold">
              {kayit.tamamlanmaTarihi
                ? fmtDate(kayit.tamamlanmaTarihi)
                : kayit.teslimat?.teslimSaati}
            </span>
          )}
        </div>

        <span className="font-mono text-[10px] text-farm-muted font-semibold">
          {kayit.hisseNo}
        </span>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-farm-cream border border-farm-border rounded px-2.5 py-1">
            <i className="ri-leaf-line text-farm-earth text-xs" />
            <span className="font-mono text-xs font-bold text-farm-brown">
              {kayit.hayvanKupeNo}
            </span>
          </div>

          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded ${
              kayit.hayvanTur === "Büyükbaş"
                ? "bg-farm-earth-pale text-farm-earth"
                : "bg-farm-green-pale text-farm-green"
            }`}
          >
            {kayit.hayvanTur}
          </span>

          <span className="text-xs text-farm-muted">{kayit.hayvanKg} kg</span>

          {kayit.hisseSayisi > 1 && (
            <span className="text-xs text-farm-muted">· {kayit.hisseSayisi} hisse</span>
          )}
        </div>

        {!isCompleted && (
          <div className="flex items-center gap-0">
            {STEPS.map((step, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;

              return (
                <div key={step.label} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
                        done
                          ? "bg-farm-green border-farm-green text-white"
                          : active
                          ? "bg-farm-earth border-farm-earth text-white"
                          : "bg-farm-cream border-farm-border text-farm-muted"
                      }`}
                    >
                      <i className={`${step.icon} text-xs`} />
                    </div>
                    <span
                      className={`text-[9px] mt-0.5 font-medium whitespace-nowrap ${
                        active
                          ? "text-farm-earth font-bold"
                          : done
                          ? "text-farm-green"
                          : "text-farm-muted"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>

                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-0.5 mb-3 ${
                        i < stepIdx ? "bg-farm-green" : "bg-farm-border"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {kayit.teslimat && <DeliveryTracker teslimat={kayit.teslimat} />}

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-farm-muted">Ödeme Durumu</span>
            <span
              className={`text-xs font-bold ${
                kayit.kalan > 0 ? "text-farm-earth" : "text-farm-green"
              }`}
            >
              {kayit.kalan > 0
                ? `${fmt(kayit.kalan)} kalan`
                : "Tüm ödemeler tamamlandı"}
            </span>
          </div>

          <div className="h-2 bg-farm-cream rounded-full overflow-hidden border border-farm-border">
            <div
              className={`h-full rounded-full transition-all ${
                kayit.kalan > 0 ? "bg-farm-earth" : "bg-farm-green"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-farm-muted">
              Ödenen:{" "}
              <span className="font-semibold text-farm-brown">{fmt(kayit.odenen)}</span>
            </span>
            <span className="text-[10px] text-farm-muted">
              Toplam:{" "}
              <span className="font-semibold text-farm-brown">
                {fmt(kayit.toplamTutar)}
              </span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          <div className="bg-farm-cream rounded px-2 py-1.5 text-center">
            <p className="text-[10px] text-farm-muted">Et</p>
            <p className="text-xs font-semibold text-farm-brown">
              {fmt(kayit.etTutari)}
            </p>
          </div>

          <div className="bg-farm-cream rounded px-2 py-1.5 text-center">
            <p className="text-[10px] text-farm-muted">Hizmet</p>
            <p className="text-xs font-semibold text-farm-brown">
              {fmt(hizmetTutar)}
            </p>
          </div>

          <div
            className={`rounded px-2 py-1.5 text-center ${
              kayit.kalan > 0 ? "bg-farm-earth-pale" : "bg-farm-green-pale/50"
            }`}
          >
            <p className="text-[10px] text-farm-muted">Kalan</p>
            <p
              className={`text-xs font-bold ${
                kayit.kalan > 0 ? "text-farm-earth" : "text-farm-green"
              }`}
            >
              {kayit.kalan > 0 ? fmt(kayit.kalan) : "—"}
            </p>
          </div>
        </div>

        <div>
          <button
            onClick={() => setShowOdemeler((v) => !v)}
            className="flex items-center gap-1 text-[10px] font-semibold text-farm-muted hover:text-farm-brown cursor-pointer transition-colors"
          >
            <i
              className={`${
                showOdemeler ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
              } text-sm`}
            />
            Ödeme Geçmişi ({kayit.odemeler.length} kayıt)
          </button>

          {showOdemeler && (
            <div className="mt-2 space-y-1.5">
              {kayit.odemeler.map((o, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-[10px] bg-farm-cream rounded px-2.5 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-farm-green text-xs" />
                    <span className="font-mono text-farm-muted">
                      {new Date(o.tarih).toLocaleDateString("tr-TR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </span>
                    <span className="text-farm-brown">{o.aciklama}</span>
                  </div>
                  <span className="font-bold text-farm-green">{fmt(o.miktar)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}